import type { SelectProps } from '@byecode/ui'
import { Group, IconFont, Input, SegmentedControl, Slider, Text } from '@byecode/ui'
import type { RectSizeUnit, TextProtocol } from '@lighthouse/core'
import { findNodeDom, getFontSizeByPageStyle, ListItem4ByecodeUi, PAGE_MODAL } from '@lighthouse/shared'
import { useUncontrolled } from '@lighthouse/tools'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { lastPageOfStackAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'

import { FontFamilyControl } from '../BlockSettings/Common/FontFamilyControl'
import { InheritLabel } from '../InheritLabel'
import { LayoutSizeInput } from '../StyleSetting/AutoLayoutSetting/LayoutSizeInput'

const Container = styled.div``
const CollapseButton = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    appearance: none;
    display: flex;
    align-items: center;
    background-color: rgba(38, 65, 90, 0.06);
    padding: 6px;
    border-radius: 50%;
    &:hover {
        background-color: var(--color-gray-200);
    }
`

export interface TextControllerConfigure {
    collapsible?: boolean

    enableFontStyle?: boolean

    enableFontWeight?: boolean

    enableFontFamily?: boolean

    enableFontSize?: boolean

    enableLineHeight?: boolean

    enableLetterSpacing?: boolean

    enableLineClamp?: boolean
}

interface TextControllerProps extends TextControllerConfigure {
    defaultValue?: TextProtocol
    value?: TextProtocol
    onChange?: (value: TextProtocol) => void
    onChangeEnd?: (value: TextProtocol) => void

    fontFamilyInputProps?: SelectProps

    notForm?: boolean
}

const FONT_STYLE_OPTIONS = [
    {
        label: '常规体',
        value: 'normal'
    },
    {
        label: '斜体',
        value: 'italic'
    }
]

export const TextController = ({
    defaultValue,
    value,
    onChange,
    onChangeEnd,
    collapsible,
    fontFamilyInputProps,
    enableFontStyle,
    enableFontWeight,
    enableFontFamily,
    enableFontSize,
    enableLineHeight,
    enableLetterSpacing,
    enableLineClamp,
    notForm
}: TextControllerProps) => {
    const [_value, _onChange] = useUncontrolled({ value, onChange, defaultValue, finalValue: {} })
    const { fontFamily, fontStyle, fontWeight, fontSize, lineHeight, letterSpacing, lineClamp } = _value

    const [draftLineClamp, setDraftLineClamp] = useState(lineClamp ? lineClamp.toString() : '')

    const handleLineClampSave = (v: number | undefined) => {
        setDraftLineClamp(v ? v.toString() : '')
        _onChange?.({ ..._value, lineClamp: v })
        onChangeEnd?.({ ..._value, lineClamp: v })
    }

    useUpdateEffect(() => {
        setDraftLineClamp(lineClamp ? lineClamp.toString() : '')
    }, [lineClamp])

    const onUnitChange = useAtomCallback(
        useCallback((get, set, unit: Exclude<RectSizeUnit, 'vw' | 'vh'>, prevValue?: TextProtocol['fontSize']) => {
            const prevUnit = prevValue?.unit || 'px'
            const prevSize = prevValue?.size || 16

            if (prevUnit === unit) {
                return { size: prevSize, unit }
            }

            const stack = get(lastPageOfStackAtom)
            const pageInternalState = stack?.stackId ? get(pageInternalStateAtomFamily(stack.stackId)) : undefined
            const selectedNode = pageInternalState?.selectedNodes?.[0]

            const targetDom = selectedNode
                ? findNodeDom(selectedNode, document.querySelector(`.${PAGE_MODAL}`))?.parentElement
                : findNodeDom({ id: 'root' }, document.querySelector(`.${PAGE_MODAL}`))?.parentElement
            if (!targetDom) {
                return { size: prevSize, unit }
            }

            const style = window.getComputedStyle(targetDom)
            const parentFontSize = Number.parseFloat(style.fontSize)

            switch (unit) {
                case '%': {
                    if (prevUnit === 'px') {
                        const size = Number(((prevSize * 100) / parentFontSize).toFixed(2))
                        return { size, unit }
                    }
                    if (prevUnit === 'rem') {
                        const rootFontSize = getFontSizeByPageStyle() || 0
                        const size = Number(((rootFontSize * prevSize * 100) / parentFontSize).toFixed(2))
                        return { size, unit }
                    }
                    break
                }

                case 'rem': {
                    const rootFontSize = getFontSizeByPageStyle() || 0

                    if (prevUnit === '%') {
                        const size = Number((Math.round((prevSize * parentFontSize) / 100) / rootFontSize).toFixed(2))
                        return { size, unit }
                    }
                    if (prevUnit === 'px') {
                        const size = Number((prevSize / rootFontSize).toFixed(2))
                        return { size, unit }
                    }
                    break
                }

                case 'px': {
                    if (prevUnit === '%') {
                        const size = Math.round((prevSize * parentFontSize) / 100)
                        return { size, unit }
                    }
                    if (prevUnit === 'rem') {
                        const rootFontSize = getFontSizeByPageStyle() || 0
                        const size = Math.round(prevSize * rootFontSize)
                        return { size, unit }
                    }
                    break
                }

                default: {
                    break
                }
            }

            return { size: prevSize, unit }
        }, [])
    )

    const dom = (
        <>
            {enableFontSize && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>大小</Text> : <InheritLabel label="大小" name="breakPoint.font.fontSize" />}

                    <LayoutSizeInput
                        iconType="TextAa"
                        onUnitChange={onUnitChange}
                        value={fontSize}
                        onChange={v => _onChange?.({ ..._value, fontSize: v })}
                    />
                    {/* <Slider
                        value={fontSize}
                        onChange={v => _onChange?.({ ..._value, fontSize: v })}
                        onChangeEnd={v => onChangeEnd?.({ ..._value, fontSize: v })}
                        min={0.5}
                        max={50}
                        step={0.125}
                        style={{ width: 180 }}
                        inputProps={{
                            onFocus: e => {
                                e.target.select()
                            }
                        }}
                    /> */}
                </ListItem4ByecodeUi>
            )}
            {enableLineHeight && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>行间距</Text> : <InheritLabel label="行间距" name="breakPoint.font.lineHeight" />}
                    <Slider
                        value={lineHeight}
                        onChange={v => _onChange?.({ ..._value, lineHeight: v })}
                        onChangeEnd={v => onChangeEnd?.({ ..._value, lineHeight: v })}
                        min={0.75}
                        max={2.5}
                        step={0.125}
                        style={{ width: 180 }}
                        inputProps={{
                            onFocus: e => {
                                e.target.select()
                            }
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {enableLetterSpacing && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>字间距</Text> : <InheritLabel label="字间距" name="breakPoint.font.letterSpacing" />}
                    <Slider
                        value={letterSpacing}
                        onChange={v => _onChange?.({ ..._value, letterSpacing: v })}
                        onChangeEnd={v => onChangeEnd?.({ ..._value, letterSpacing: v })}
                        min={-0.5}
                        max={1.5}
                        step={0.125}
                        style={{ width: 180 }}
                        inputProps={{
                            onFocus: e => {
                                e.target.select()
                            }
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {/* <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>超出隐藏</Text>
                <Switch
                    checked={ellipsis}
                    onChange={e => {
                        _onChange?.({ ..._value, ellipsis: e.target.checked })
                        onChangeEnd?.({ ..._value, ellipsis: e.target.checked })
                    }}
                />
            </ListItem4ByecodeUi> */}

            {enableLineClamp && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>最多行数</Text> : <InheritLabel label="最多行数" name="breakPoint.font.lineClamp" />}
                    <Input
                        value={draftLineClamp}
                        onChange={e => setDraftLineClamp(e.target.value)}
                        onBlur={e => {
                            const { value } = e.currentTarget
                            if (!value) {
                                handleLineClampSave(undefined)
                                return
                            }
                            const number = Number(value)
                            handleLineClampSave(Number.isNaN(number) || number <= 0 ? lineClamp : number)
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                const { value } = e.currentTarget
                                if (!value) {
                                    handleLineClampSave(undefined)
                                    return
                                }
                                const number = Number(value)
                                handleLineClampSave(Number.isNaN(number) || number <= 0 ? lineClamp : number)
                            }
                        }}
                        onFocus={e => {
                            e.target.select()
                        }}
                        placeholder="不限制"
                        style={{ width: 180 }}
                    />
                </ListItem4ByecodeUi>
            )}
        </>
    )

    return (
        <Container>
            {enableFontStyle && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>风格</Text> : <InheritLabel label="风格" name="breakPoint.font.fontStyle" />}
                    <SegmentedControl
                        value={fontStyle}
                        onChange={v => {
                            _onChange?.({ ..._value, fontStyle: v })
                            onChangeEnd?.({ ..._value, fontStyle: v })
                        }}
                        fullWidth
                        data={FONT_STYLE_OPTIONS}
                        style={{ width: 180 }}
                    />
                </ListItem4ByecodeUi>
            )}
            {enableFontWeight && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    {notForm ? <Text>字重</Text> : <InheritLabel label="字重" name="breakPoint.font.fontWeight" />}
                    <Slider
                        defaultValue={400}
                        value={fontWeight}
                        onChange={v => _onChange?.({ ..._value, fontWeight: v })}
                        onChangeEnd={v => onChangeEnd?.({ ..._value, fontWeight: v })}
                        min={100}
                        max={900}
                        step={100}
                        style={{ width: 180 }}
                        inputProps={{
                            onFocus: e => {
                                e.target.select()
                            }
                        }}
                    />
                </ListItem4ByecodeUi>
            )}
            {enableFontFamily && (
                <FontFamilyControl
                    value={fontFamily}
                    notForm={notForm}
                    name="breakPoint.font.fontFamily"
                    fontFamilyInputProps={fontFamilyInputProps}
                    onChange={v => _onChange({ ..._value, fontFamily: v })}
                    onChangeEnd={v => onChangeEnd?.({ ..._value, fontFamily: v })}
                />
            )}
            {collapsible ? (
                <Group
                    label="更多配置"
                    renderRightIcon={(open, onChange) => {
                        return (
                            <CollapseButton onClick={() => onChange(!open)}>
                                <IconFont
                                    type="ArrowDownSmall"
                                    size={16}
                                    color="var(--color-gray-400)"
                                    style={{ transform: open ? 'rotate(-180deg)' : undefined, transition: 'transform 0.2s' }}
                                />
                            </CollapseButton>
                        )
                    }}
                    styles={{
                        collapse: { padding: '0!important' },
                        label: { marginLeft: 'auto', fontWeight: 400 },
                        rightIcon: { marginLeft: 'unset' }
                    }}
                >
                    {dom}
                </Group>
            ) : (
                dom
            )}
        </Container>
    )
}
